import React, { useEffect } from "react";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { appRouteEnums, menuTitleEnums } from "@enums";
import { StyleTimeSheet, TimeSheetCalendarComponent, TimeSheetToggleModal } from ".";
import {
  CircularLoader,
  TitleMenuComponent,
  AppCustomeSelect,
  AppExportCsv,
  PassChangeButton,
} from "..";
import { selectAuth, useAppSelector, selectGroupsLoading, selectGroupsData, selectGroupsError, selectEmployeesData, selectTimesheetsDataByMonth, fetchTimesheets } from "@redux";
import { checkExistUserSelected, linkImage } from "@utils";
import { StyledGrayToggleButton } from "../WeeklySchedule/styles";
import { weeklyScheduleApi } from "src/Api/weekly-schedule";
import { useSortedUserList } from "src/Utils/Hooks/useSorter";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useGroupSelectionHistories } from "src/Utils/Hooks/useGroupSelectionHistories";

const defaultGroup = {
  id: 0,
  name: "全て",
};

export const TimeSheetView = () => {
  const auth = useAppSelector(selectAuth);
  const id = auth?.userInfo?.id;

  // hooks
  const dispatch = useDispatch();
  const history = useHistory();

  const currentDate = new Date();
  const initialYear = currentDate.getFullYear();
  const initialMonth = String(currentDate.getMonth() + 1).padStart(2, '0'); // Ensure 2 digits
  const initialCalendarMonth = (`${initialYear}-${initialMonth}`);
  const initialCsvStartDate = new Date(Date.UTC(initialYear, currentDate.getMonth(), 1));
  const initialCsvEndDate = new Date(Date.UTC(initialYear, currentDate.getMonth() + 1, 0));

  // component state
  const [timeSheetDateData, setTimeSheetDateData] = React.useState<any>();
  const [timeSheetIdData, setTimeSheetIdData] = React.useState<any>();
  const [timesheetListData, setTimeSheeListData] = React.useState<any>([]);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [inputValue, setInputValue] = React.useState<any>();
  const [inputGroupValue, setInputGroupValue] = React.useState<any>();
  const [inputStatusValue, setInputStatusValue] = React.useState<any>();
  const [currentMonth, setCurrentMonth] = React.useState(initialCalendarMonth); // YYYY-MM format
  const [loading, setLoading] = React.useState<boolean>();
  const [groups, setGroups] = React.useState<any>([]);
  const [selectedGroup, setSelectedGroup] = React.useState<any>([]);
  const [users, setUsers] = React.useState<any>([]);
  const [cvsStartDate, setCvsStartDate] = React.useState<string>(initialCsvStartDate.toISOString().split('T')[0]);
  const [cvsEndDate, setCvsEndDate] = React.useState<string>(initialCsvEndDate.toISOString().split('T')[0]);
  const [options, setOptions] = React.useState<{ label: string; value: string, type: string }[]>([]);
  const sortedUsers = useSortedUserList(users, true, true, "Timesheet");
  const [timeSeetGroupIdData, setTimeSheetGroupIdData] = React.useState<any>();

  const [hasAutoSelected, setHasAutoSelected] = React.useState<boolean>(false);

  const statusOptions: { label: string; value: string }[] = [
    { label: "全て", value: "all" },
    { label: "未承認", value: "pending" },
    { label: "承認済み", value: "approved" },
    { label: "差し戻し", value: "rejected" }
  ];

  // Redux selectors for groups, timesheets, and employees
  const groupsLoading = useAppSelector(selectGroupsLoading);
  const groupsData = useAppSelector(selectGroupsData);
  const groupsError = useAppSelector(selectGroupsError);
  const timesheetsDataForMonth = useAppSelector((state) => selectTimesheetsDataByMonth(state, currentMonth));
  const employeesData = useAppSelector(selectEmployeesData);

  const { list, fetchList, storeSelection } = useUserSelectionHistories(id ?? 0);
  const { groupList, fetchGroupList, storeGroupSelection } = useGroupSelectionHistories(id ?? 0);

  useEffect(() => {
    if (groupsData) { setGroups(groupsData); }
  }, [groupsData]);

  useEffect(() => {
    if (employeesData) {
      setUsers(employeesData);
    }
  }, [employeesData]);

  // Check if timesheets for the selected month exist
  React.useEffect(() => {
    if (timesheetsDataForMonth) {
      const selectedTSStatus = inputStatusValue && inputStatusValue?.value || null;

      let filteredTimesheets = filterTimesheets(timesheetsDataForMonth);

      if (selectedTSStatus) {
        filteredTimesheets = (filterTimesheetsStatus(filteredTimesheets, inputStatusValue.value));

      }
      // If the data is already available, use it directly
      setTimeSheeListData(filteredTimesheets);
      setLoading(false);
    } else {
      // If the data is not available, dispatch fetchTimesheets to load it
      dispatch(fetchTimesheets({ month: currentMonth }));

      // Set loading state while fetching data
      setLoading(true);
    }
  }, [currentMonth, timesheetsDataForMonth, inputValue?.value, inputGroupValue?.value, inputStatusValue?.value, selectedGroup]);

  //既存のユーザーリスト読み込みは停止
  // useEffect(() => {
  //   if (users && groups) {
  //     const userOption = [...sortedUsers];
  //     const groupOption = groups.map((group) => ({
  //       label: group.name,
  //       value: group.id.toString(),
  //       type: "group",
  //     }));

  //     setOptions([...userOption, ...groupOption]);
  //     setInputValue(userOption[0]);
  //   }
  // }, [users, groups, sortedUsers]);

  //ユーザーとグループのリアルタイム履歴反映のリスト読み込み
    useEffect(() => {
      if (list && groupList) {
        
        const userOption = list.map((user) => ({
          label: `${user.last_name} ${user.first_name}`,
          value: user.id.toString(),
          type: user.type,
        }));
        
        const currentUserOption = userOption.find((user) => user.value === id?.toString());
  
        const userOptionWithoutCurrentUser = userOption.filter((user) => user.value !== id?.toString());
        
        const groupOption = groupList.map((group) => ({
          label: group.name,
          value: group.id.toString(),
          type: group.type,
        }));
  
        setOptions(
          [currentUserOption, ...userOptionWithoutCurrentUser, ...groupOption].filter(
            (option): option is { label: string; value: string; type: string } => option !== undefined
          )
        );
  
        // 初回アクセス時のみログインユーザー既存選択固定
        if (!hasAutoSelected && currentUserOption) {
          setInputValue(currentUserOption);
          setHasAutoSelected(true);
        }
  
      }
    }, [list, groupList]);

  // events
  const handleOpenModal = (date: any, id: number) => {
    setOpenModal(true);
    setTimeSheetDateData({
      date: date.startDate[0],
    });
    setTimeSheetIdData({
      id,
    });
  };
  const handleOpenModalId = (date: any, id: number, groupId: number) => {
    setOpenModal(true);
    setTimeSheetDateData({
      date: date.startDate,
    });
    setTimeSheetIdData({
      id,
    });
    setTimeSheetGroupIdData({ groupId });
  };

  const handleCloseToggle = () => {
    setOpenModal(false);
    setTimeSheetDateData([]);
    setTimeSheetIdData("");
    setTimeSheetGroupIdData("");
  };

  /**
   * 月選択の変更を処理する
   * @param event - 月選択の入力イベント
   */
  const handleMonthSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = event.target.value; // The value will be in 'YYYY-MM-DD' format
    const selectedMonth = selectedDate.slice(0, 7); // Extract the month part
    setCurrentMonth(selectedMonth);
  };

  const handleSelectGroup = (id) => {
    setInputValue("");
    if (id === 0) {
      setSelectedGroup([]);
      setTimeSheeListData(timesheetsDataForMonth);
      return;
    }
    const newSelectedGroup = checkExistUserSelected(id, selectedGroup) ? selectedGroup.filter((e) => e !== id) : [...selectedGroup, id];
    setSelectedGroup(newSelectedGroup);
    setTimeSheeListData(filterTimesheets(timesheetsDataForMonth, newSelectedGroup));
  };

  /**
   * 人・班コンボボックス変更時に呼ばれる関数。
   * ユーザーまたはグループの選択に応じて、タイムシートデータをフィルタリングする
   * 
   * @param {Object} value - 選択された値
   * @param {string} value.type - 選択タイプ（'user' または 'group'）
   * @param {string} value.value - 選択された値（ユーザーIDまたはグループID）
   * 
   * 処理内容:
   * 1. 値が空の場合: 全てのフィルターをリセット
   * 2. ユーザー選択の場合: グループ選択をリセットし、ユーザーでフィルタリング
   * 3. グループ選択の場合: ユーザー選択をリセットし、グループでフィルタリング
   *    - グループIDが0の場合: 全てのグループを表示
   *    - それ以外: 選択されたグループのみを表示
   * 
   * @example
   * // ユーザー選択の場合
   * handleUserOrGroupSelection({ type: 'user', value: '123' })
   * 
   * // グループ選択の場合
   * handleUserOrGroupSelection({ type: 'group', value: '456' })
   */
  const handleUserOrGroupSelection = (value) => {
    if (!value) {
      setInputValue(null);
      setInputGroupValue(null);
      setSelectedGroup([]);
      return;
    }

    if (value.type === "user") {
      setSelectedGroup([]);
      setInputValue(value);
      weeklyScheduleApi.updateOrderListName(value.value);
      //選択したユーザーの履歴保存
      storeSelection({ user_id: id, selected_user_id: value.value });
    }

    if (value.type === "group") {
      setInputValue("");
      setInputGroupValue(value);
      setSelectedGroup([]);
      const groupId = parseInt(value.value);

      if (groupId === 0) {
        setSelectedGroup([]);
        setTimeSheeListData(timesheetsDataForMonth);
        return;
      }

      //選択したグループの履歴保存
      storeGroupSelection({ user_id: id, selected_group_id: value.value });

      setSelectedGroup(() => {
        const updatedGroup = [groupId];
        setTimeSheeListData(filterTimesheets(timesheetsDataForMonth, updatedGroup));

        return updatedGroup;
      });
    }
  };

  const handleOnChangeStatus = (value) => {
    if (!value) {
      setInputStatusValue(null);
      return;
    }

    setInputStatusValue(value);
  };

  const updateUserSessionStorage = (userId: number) => {
    const stored = sessionStorage.getItem("userListOrderTimesheet");
    let list: number[] = [];

    try {
      if (stored) {
        list = JSON.parse(stored);
        if (!Array.isArray(list)) list = [];
      }
    } catch {
      list = [];
    }

    // Remove userId if exists, then add to front
    list = list.filter((id) => id !== userId);
    list.unshift(userId);

    sessionStorage.setItem("userListOrderTimesheet", JSON.stringify(list));
  };

  const filterTimesheetsStatus = (timesheets, selectedStatus) => {
    const status = selectedStatus === "approved" ? 1 : selectedStatus === "rejected" ? 2 : selectedStatus === "pending" ? 0 : 3;
    return timesheets.map((day) => {
      const filteredUsers = day.users.filter((user) => {

        const userHasMatchingTimesheet = user.timesheets.some((timesheet) => {
          const isDateMatching = timesheet.date === day.startDate;

          let isStatusMatching = true;

          if (status !== 3) {
            isStatusMatching = status === timesheet.status;
          }

          return isDateMatching && isStatusMatching;

        });

        return userHasMatchingTimesheet;
      });

      return {
        ...day,
        users: filteredUsers,
      };
    });
  };

  const filterTimesheets = (timesheets, mewSelectedGroup = selectedGroup) => {
    return timesheets.map((day) => {
      // Filter users for the current day
      const filteredUsers = day.users.filter((user) => {
        // Filter the user's timesheets based on date and group_id conditions
        const isUserMatching = !inputValue || parseInt(inputValue.value) === user.id;

        const userHasMatchingTimesheet = user.timesheets.some((timesheet) => {
          // Condition 1: The timesheet date must match the day's startDate
          const isDateMatching = timesheet.date === day.startDate;

          // Condition 2: If no groups are selected, all timesheets are valid.
          // Otherwise, only timesheets with a group_id in selectedGroup should be considered.
          const isGroupMatching =
            mewSelectedGroup.length === 0 || mewSelectedGroup.includes(timesheet.group_id);

          return isDateMatching && isGroupMatching;
        });

        // Include the user only if they have at least one matching timesheet
        return isUserMatching && userHasMatchingTimesheet;
      });

      // Return a new day object with filtered users. If no users match, this day will have an empty users array.
      return {
        ...day,
        users: filteredUsers,
      };
    });
  };

  const groupsContent = groupsLoading ? (
    <span>Loading...</span>
  ) : groupsError ? (
    <div> Groups error: {groupsError} </div>
  ) : (
    groups.map((item) => (
      <StyledGrayToggleButton
        key={item.id}
        isActive={checkExistUserSelected(item.id, selectedGroup)}
        onClick={() => handleSelectGroup(item.id)}
      >
        {item.name}
      </StyledGrayToggleButton>
    ))
  );

  return (
    <StyleTimeSheet>
      {/* <AppTopBar /> */}
      <TimeSheetToggleModal
        openToogle={openModal}
        anchor="right"
        handleCloseToggle={handleCloseToggle}
        timeSheetDateData={timeSheetDateData}
        // id={timeSheetIdData}
        id={inputValue?.value ?? null}
        // selectedGroup={selectedGroup}
        groupId={inputGroupValue?.value ?? null}
        status={inputStatusValue?.value ?? null}
      />
      <CircularLoader loading={loading} />
      <div className="timesheet">
        <div className="timesheet__title to-link">
          <TitleMenuComponent text={menuTitleEnums.TITLE_TIMESHEET} />
        </div>
        <div className="timesheet__datePicker">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "1rem" }}>
            <div className="label">月を選択</div>
            <div className="wrapperDatePicker">
              <input
                type="month"
                value={currentMonth}
                onChange={handleMonthSelection}
                className="inputMonth"
              />
            </div>
          </div>
          <div className="timesheet__search">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "1rem" }}>
              <div className="label">班・従業員</div>
              <div className="wrapperSelectGroup">
                <AppCustomeSelect
                  placeholder="人・班を選択"
                  onChange={handleUserOrGroupSelection}
                  groupUserOptions={options}
                  options={options}
                  value={(inputValue === "") ? inputGroupValue : inputValue}
                  isClearable
                />
              </div>
            </div>
          </div>

          <div className="timesheet__search">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "1rem" }}>
              <div className="label">ステータス</div>
              <div className="wrapperSelectGroup">
                <AppCustomeSelect
                  placeholder="ステータスを選択"
                  onChange={handleOnChangeStatus}
                  groupUserOptions={statusOptions}
                  options={statusOptions}
                  value={inputStatusValue}
                  isClearable
                />
              </div>
            </div>
          </div>
        </div>
        <div className="timesheet__form">
        </div>

        <div className="timesheet__calendar">
          <TimeSheetCalendarComponent
            handleOpenModal={handleOpenModal}
            handleOpenModalId={handleOpenModalId}
            handleCloseToggle={handleCloseToggle}
            timeSheetData={timesheetListData}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
          />
        </div>

        <div className="csvExportDiv">
          <span className="csvExportTitle">CSVエクスポート</span>
          <div className="tsDatePicker">

            <div className="dateContainer">

              <div className="label">作成日時</div>
              <div className="dateSubContainer">
                <input type="date" className="inputDate" value={cvsStartDate} onChange={(e) => setCvsStartDate(e.target.value)} />
                <span>から</span>
              </div>

              <div className="dateSubContainer">
                <input type="date" className="inputDate" value={cvsEndDate} onChange={(e) => setCvsEndDate(e.target.value)} />
                <span>まで</span>
              </div>
            </div>

            <div>
              <AppExportCsv type={"timesheets"} startDate={cvsStartDate} endDate={cvsEndDate} />
            </div>
          </div>
        </div>
      </div>
    </StyleTimeSheet>
  );
};
